import React, { useState, useEffect } from 'react';
import { Play, FastForward, XCircle, AlertTriangle, Mic, Paperclip, ZoomIn, RefreshCw, Smartphone } from 'lucide-react';
import './InterventionModal.css';

/**
 * InterventionModal - Displays when AI needs human intervention
 * 
 * Handles:
 * - CAPTCHAs
 * - Payment confirmations
 * - Destructive actions
 * - 2FA/authentication
 * - Stuck in loop
 */
function InterventionModal({ request, onRespond, open }) {
    const [selectedOption, setSelectedOption] = useState(null);
    const [interventionInput, setInterventionInput] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedOption(null);
            setInterventionInput('');
        }
    }, [open]);

    if (!open || !request) return null;

    const getSeverityIcon = () => {
        switch (request.severity) {
            case 'critical':
                return '🚨';
            case 'high':
                return '⚠️';
            default:
                return 'ℹ️';
        }
    };

    const getSeverityClass = () => {
        switch (request.severity) {
            case 'critical':
                return 'severity-critical';
            case 'high':
                return 'severity-high';
            case 'medium':
                return 'severity-medium';
            default:
                return 'severity-default';
        }
    };

    const getReasonLabel = () => {
        const labels = {
            'captcha_detected': 'CAPTCHA Detected',
            'payment_action': 'Payment Action',
            'destructive_action': 'Destructive Action',
            'authentication_required': '2FA Required',
            'two_factor_auth': '2FA Required',
            'stuck_in_loop': 'AI Stuck in Loop',
            'external_action_required': 'Enternal action required',
            'ai_requested': 'AI Requested'
        };
        return labels[request.reason] || 'Human Intervention Required';
    };

    const getButtonClass = (option) => {
        if (option.color === 'error') return 'btn-danger';
        if (option.color === 'warning') return 'btn-warning';
        return 'btn-primary';
    };

    return (
        <div className="intervention-modal-overlay">
            <div className="intervention-modal">
                <div className="modal-top-bar"></div>
                <div className="modal-header">
                    <div className="header-icon-box">
                        <AlertTriangle size={24} color="#f59e0b" weight="fill" />
                    </div>
                    <div>
                        <h2>AI Agent Paused: Manual Action Required</h2>
                        {/* <span className="protocol-badge">
                            INTERVENTION PROTOCOL LEVEL {request.severity === 'critical' ? '3' : request.severity === 'high' ? '2' : '1'}
                        </span> */}
                    </div>
                </div>

                <div className="modal-content-split">
                    {/* Left Column: Details & Controls */}
                    <div className="modal-left-panel">
                        <div className="panel-section">
                            <h4 className="section-title">CURRENT STATUS</h4>
                            <div className="status-box">
                                <div className="status-icon-badge">
                                    <div className="nav-arrow" />
                                </div>
                                <div>
                                    <h5 className="status-label">{getReasonLabel()}</h5>
                                    <span className="status-sub">
                                        Step action paused awaiting input
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="panel-section">
                            <h4 className="section-title">REASON FOR PAUSE</h4>
                            <div className="reason-box">
                                <p>
                                    <strong className="text-purple">AI Reasoning:</strong> {request.message || request.decision?.reasoning || 'The automated flow cannot proceed without manual verification.'}
                                </p>
                                {/* <div className="reason-tags">
                                    <span className="tag">{request.reason.toUpperCase()}</span>
                                    {request.decision?.confidence && (
                                        <span className="tag">CONFIDENCE_SCORE: {request.decision.confidence.toFixed(2)}</span>
                                    )}
                                </div> */}
                            </div>
                        </div>

                        <div className="panel-section">
                            <h4 className="section-title">PROVIDE GUIDANCE</h4>
                            <div className="guidance-input-wrapper">
                                <textarea
                                    className="guidance-textarea"
                                    placeholder="(Optional) Provide input for guidance here..."
                                    value={interventionInput}
                                    onChange={(e) => setInterventionInput(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="action-buttons-grid">
                            {request.options?.find(o => o.action === 'continue' || o.action === 'manual_solve') && (
                                <button
                                    className={`btn btn-primary btn-resume ${selectedOption === 'continue' ? 'selected' : ''}`}
                                    onClick={() => {
                                        const opt = request.options.find(o => o.action === 'continue' || o.action === 'manual_solve');
                                        setSelectedOption(opt.action);
                                        onRespond(opt.action, interventionInput);
                                    }}
                                    disabled={selectedOption !== null && selectedOption !== 'continue'}
                                >
                                    <Play size={18} fill="currentColor" /> Continue
                                </button>
                            )}

                            {request.options?.find(o => o.action === 'skip') && (
                                <button
                                    className={`btn btn-secondary btn-skip ${selectedOption === 'skip' ? 'selected' : ''}`}
                                    onClick={() => {
                                        setSelectedOption('skip');
                                        onRespond('skip', interventionInput);
                                    }}
                                    disabled={selectedOption !== null && selectedOption !== 'skip'}
                                >
                                    <FastForward size={18} fill="currentColor" /> Skip
                                </button>
                            )}

                            {request.options?.find(o => o.action === 'abort') && (
                                <button
                                    className={`btn btn-danger btn-abort ${selectedOption === 'abort' ? 'selected' : ''}`}
                                    onClick={() => {
                                        setSelectedOption('abort');
                                        onRespond('abort', interventionInput);
                                    }}
                                    disabled={selectedOption !== null && selectedOption !== 'abort'}
                                >
                                    <XCircle size={18} /> Abort
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Live Feed */}
                    <div className="modal-right-panel">
                        <div className="live-feed-header">
                            <h4 className="section-title">LIVE SNAPSHOT</h4>
                            <div className="live-indicator">
                                <span className="pulse-dot"></span> LIVE FEED
                            </div>
                        </div>

                        <div className="device-wrapper">
                            <div className="device-frame-mock">
                                {request.state?.screenshot ? (
                                    <img
                                        src={`data:image/png;base64,${request.state.screenshot}`}
                                        alt="Current app screen"
                                        className="device-screen-img"
                                    />
                                ) : (
                                    <div className="no-screenshot">No screen available</div>
                                )}
                            </div>

                            {/* <div className="device-controls">
                                <button className="device-control-btn"><ZoomIn size={14} /></button>
                                <button className="device-control-btn"><RefreshCw size={14} /></button>
                                <button className="device-control-btn"><Smartphone size={14} /></button>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InterventionModal;
